import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { RingSizes, RingType } from 'content/products/ring/productMap/enums';

export { RingSizes, RingType };

/**
 * Ring PRO ring colors (4). Names TBD / to be renamed later.
 */
export enum RingProColors {
  GOLD = 'GOLD',
  SILVER = 'SILVER',
  BLACK = 'BLACK',
  TITANIUM = 'TITANIUM',
}

/** Color ids `ring_order` and the order-edit API expect for Ring PRO. */
export const RING_PRO_COLOR_BACKEND_ID: Record<RingProColors, string> = {
  [RingProColors.GOLD]: 'pro_bionic_gold',
  [RingProColors.SILVER]: 'pro_space_silver',
  [RingProColors.BLACK]: 'pro_aster_black',
  [RingProColors.TITANIUM]: 'pro_raw_titanium',
};

export function ringProColorFromBackendId(id: string): RingProColors | null {
  const match = (
    Object.keys(RING_PRO_COLOR_BACKEND_ID) as RingProColors[]
  ).find((key) => RING_PRO_COLOR_BACKEND_ID[key] === id);
  return match ?? null;
}

/**
 * Map a Ring PRO colour identifier (e.g. `pro_raw_titanium`) or display name
 * (e.g. `Raw Titanium`) to a `RingProColors` key via substring match. Used by
 * the order tracking page (asset selection) and the variant edit modal so the
 * mapping lives in one place.
 */
export const ringProColorKeyFromIdentifier = (
  identifier?: string | null,
  ringColorName?: string | null,
): RingProColors => {
  const id = (identifier || ringColorName || '').toLowerCase();
  if (id.includes('silver')) return RingProColors.SILVER;
  if (id.includes('black')) return RingProColors.BLACK;
  if (id.includes('titanium')) return RingProColors.TITANIUM;
  return RingProColors.GOLD;
};

/** Hero/ring render shown on the order tracking page, keyed by colour. */
export const RING_PRO_ORDER_TRACKING_IMAGE: Record<RingProColors, string> = {
  [RingProColors.GOLD]: 'web_v2/ring-pro/order-tracking/v2/bionic-gold.png',
  [RingProColors.SILVER]: 'web_v2/ring-pro/order-tracking/v2/space-silver.png',
  [RingProColors.BLACK]: 'web_v2/ring-pro/order-tracking/v2/aster-black.png',
  [RingProColors.TITANIUM]:
    'web_v2/ring-pro/order-tracking/v2/raw-titanium.png',
};

/**
 * Ring PRO charging case colors (2 options).
 */
export enum RingProChargerCaseColor {
  GOLD = 'GOLD',
  BLACK = 'BLACK',
}

export const AUTO_PAIR_CHARGER_WITH_RING = true;

export type DisabledRingProSkuRule = {
  /** Omit = all colors */
  colors?: readonly RingProColors[];
  /** Omit = all sizes */
  sizes?: readonly RingSizes[];
  /** Omit = both ENGRAVED and NOT_ENGRAVED */
  types?: readonly RingType[];
  /** Omit = every ShopifyStore */
  stores?: readonly ShopifyStore[];
};

export type RingProSkuQuery = {
  color?: RingProColors;
  size?: RingSizes;
  type?: RingType;
  store?: ShopifyStore | null;
};

/**
 * Disable SKUs by color / size / engraving / store. Omitted fields are wildcards.
 * Remove a rule (or empty the list) to re-enable.
 */
export const DISABLED_RING_PRO_SKUS: readonly DisabledRingProSkuRule[] = [];

const RING_PRO_COLOR_VALUES = Object.values(RingProColors) as RingProColors[];
const RING_PRO_SIZE_VALUES = Object.values(RingSizes).filter(
  (value): value is RingSizes => typeof value === 'number',
);
const RING_PRO_TYPE_VALUES = Object.values(RingType) as RingType[];

export function ringProSkuRuleMatches(
  rule: DisabledRingProSkuRule,
  query: RingProSkuQuery,
): boolean {
  if (rule.colors) {
    if (query.color == null || !rule.colors.includes(query.color)) return false;
  }
  if (rule.sizes) {
    if (query.size == null || !rule.sizes.includes(query.size)) return false;
  }
  if (rule.types) {
    if (query.type == null || !rule.types.includes(query.type)) return false;
  }
  if (rule.stores) {
    if (query.store == null || !rule.stores.includes(query.store)) return false;
  }
  return true;
}

export function isRingProSkuDisabled(
  query: RingProSkuQuery,
  rules: readonly DisabledRingProSkuRule[] = DISABLED_RING_PRO_SKUS,
): boolean {
  return rules.some((rule) => ringProSkuRuleMatches(rule, query));
}

export function isRingProColorDisabled(
  color: RingProColors,
  store?: ShopifyStore | null,
  rules: readonly DisabledRingProSkuRule[] = DISABLED_RING_PRO_SKUS,
): boolean {
  return RING_PRO_SIZE_VALUES.every((size) =>
    RING_PRO_TYPE_VALUES.every((type) =>
      isRingProSkuDisabled({ color, size, type, store }, rules),
    ),
  );
}

export function getAvailableRingProColors(
  store?: ShopifyStore | null,
  rules: readonly DisabledRingProSkuRule[] = DISABLED_RING_PRO_SKUS,
): RingProColors[] {
  return RING_PRO_COLOR_VALUES.filter(
    (color) => !isRingProColorDisabled(color, store, rules),
  );
}

/**
 * Every Ring PRO ships with the black in-box charger, whatever the finish.
 * GOLD stays in the enum only for carts and sessions created before this change.
 */
export const RING_COLOR_TO_CHARGER_MAP: Record<
  RingProColors,
  RingProChargerCaseColor
> = {
  [RingProColors.GOLD]: RingProChargerCaseColor.BLACK,
  [RingProColors.SILVER]: RingProChargerCaseColor.BLACK,
  [RingProColors.BLACK]: RingProChargerCaseColor.BLACK,
  [RingProColors.TITANIUM]: RingProChargerCaseColor.BLACK,
};

export type RingProVariantType = {
  variantId: Partial<Record<ShopifyStore, string>>;
  checkoutUrl: Partial<Record<ShopifyStore, string>>;
  SKU?: string;
};

export type RingProChargerCaseVariantType = {
  variantId: Partial<Record<ShopifyStore, string>>;
  checkoutUrl: Partial<Record<ShopifyStore, string>>;
  price: Partial<Record<ShopifyStore, number>>;
};
